import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, FileText, Loader2, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '@/api/http-client';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { loginSchema, UFS, type LoginForm, type TipoLogin } from '@/features/auth/schemas/login.schema';
import { LogoNohra } from '@/shared/components/LogoNohra';
import { destinoSeguro, ROTAS } from '@/shared/lib/rotas';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';

/**
 * Visual portado 1:1 da V1 (`Hormonalle/src/pages/Login.tsx`) — mesmas classes,
 * mesmo gradiente, mesmos glows, mesmo toggle Email|CRMV.
 *
 * O que MUDA: a V1 não validava nada (só `required` no HTML + checagem
 * imperativa no submit). Aqui é react-hook-form + Zod, com union discriminada
 * por `tipo` — assim o modo CRMV não exige e-mail e vice-versa, sem `if` no
 * meio da validação.
 */
export function LoginPage() {
  const [verSenha, setVerSenha] = useState(false);
  const [tipo, setTipo] = useState<TipoLogin>('email');
  const { entrarPorEmail, entrarPorCrmv, autenticado, carregando } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { tipo: 'email', email: '', senha: '' } as LoginForm,
  });

  // Já logado não vê a tela de login: volta para onde queria ir.
  if (!carregando && autenticado) {
    return <Navigate to={destinoSeguro(search)} replace />;
  }

  function trocarTipo(novo: TipoLogin) {
    if (novo === tipo) return;
    setTipo(novo);
    // Limpa os campos do modo anterior — senão um CRMV digitado continuaria no
    // formulário depois de voltar para e-mail, e seria enviado sem aparecer.
    reset(
      novo === 'email'
        ? ({ tipo: 'email', email: '', senha: '' } as LoginForm)
        : ({ tipo: 'crmv', crmv: '', ufCrmv: undefined, senha: '' } as unknown as LoginForm),
    );
  }

  const aoEnviar = handleSubmit(async (dados) => {
    try {
      if (dados.tipo === 'email') {
        await entrarPorEmail(dados.email, dados.senha);
      } else {
        await entrarPorCrmv(dados.crmv, dados.ufCrmv, dados.senha);
      }
      navigate(destinoSeguro(search), { replace: true });
    } catch (erro) {
      // Cadastro que parou na etapa de verificação: quem sai e volta depois cai
      // aqui com `EMAIL_NOT_VERIFIED` (o código anterior já expirou). Em vez de
      // um erro sem saída, manda de volta para a tela de confirmação, que
      // reenvia um código novo. O e-mail vem do formulário no login por e-mail;
      // no login por CRMV, do `detalhes.email` que o backend devolve só neste
      // caso (a credencial já foi conferida).
      if (erro instanceof ApiError && erro.codigo === 'EMAIL_NOT_VERIFIED') {
        const email = dados.tipo === 'email' ? dados.email : erro.detalhes?.email?.[0];
        if (email) {
          navigate(ROTAS.verificarEmail, { state: { email }, replace: true });
          return;
        }
      }

      // Erro de servidor entra pela raiz e cai no mesmo <Alert> do topo.
      setError('root', {
        message:
          erro instanceof ApiError
            ? erro.message
            : 'Não foi possível entrar. Verifique sua conexão e tente de novo.',
      });
    }
  });

  const ufSelecionada = watch('tipo') === 'crmv' ? watch('ufCrmv' as never) : undefined;
  const ocupado = isSubmitting;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#691241] via-[#975ea1] to-[#15acb7] p-4 relative overflow-hidden">
      {/* Glows decorativos */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-[#a2195b]/30 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#15acb7]/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#975ea1]/10 rounded-full blur-3xl" />

      <Card className="w-full max-w-md bg-surface-raised/95 backdrop-blur-sm border-brand/30 shadow-2xl">
        <CardHeader className="space-y-1 pb-4">
          <div className="flex justify-center mb-2">
            <LogoNohra tamanho="xl" />
          </div>
          <CardDescription className="text-center text-muted-foreground font-medium">
            Entre com seu CRMV ou email
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={aoEnviar} className="space-y-4" noValidate>
            {errors.root?.message && (
              <Alert variant="destructive" className="bg-red-100 border-red-300 text-red-800">
                <AlertDescription>{errors.root.message}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-2 p-1 bg-brand/10 rounded-lg">
              <button
                type="button"
                onClick={() => trocarTipo('email')}
                disabled={ocupado}
                className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                  tipo === 'email' ? 'bg-brand text-white shadow-md' : 'text-brand hover:bg-brand/10'
                }`}
              >
                <Mail className="w-4 h-4" />
                Email
              </button>
              <button
                type="button"
                onClick={() => trocarTipo('crmv')}
                disabled={ocupado}
                className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                  tipo === 'crmv' ? 'bg-brand text-white shadow-md' : 'text-brand hover:bg-brand/10'
                }`}
              >
                <FileText className="w-4 h-4" />
                CRMV
              </button>
            </div>

            <input type="hidden" {...register('tipo')} value={tipo} />

            {tipo === 'email' ? (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-brand font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Digite seu email"
                  disabled={ocupado}
                  autoComplete="username"
                  className="bg-surface-raised border-brand/30 focus:border-brand focus:ring-brand/20 placeholder:text-muted-foreground"
                  {...register('email' as never)}
                />
                {'email' in errors && errors.email?.message && (
                  <p className="text-xs text-destructive">{errors.email.message}</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="crmv" className="text-brand font-medium">
                    CRMV
                  </Label>
                  <Input
                    id="crmv"
                    type="text"
                    placeholder="Número do CRMV"
                    disabled={ocupado}
                    className="bg-surface-raised border-brand/30 focus:border-brand focus:ring-brand/20 placeholder:text-muted-foreground"
                    {...register('crmv' as never)}
                  />
                  {'crmv' in errors && errors.crmv?.message && (
                    <p className="text-xs text-destructive">{errors.crmv.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="uf_crmv" className="text-brand font-medium">
                    UF
                  </Label>
                  <Select
                    value={ufSelecionada as string | undefined}
                    onValueChange={(valor) => setValue('ufCrmv' as never, valor as never)}
                    disabled={ocupado}
                  >
                    <SelectTrigger
                      id="uf_crmv"
                      className="bg-surface-raised border-brand/30 focus:border-brand focus:ring-brand/20 text-brand"
                    >
                      <SelectValue placeholder="UF" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-raised border-brand/30">
                      {UFS.map((uf) => (
                        <SelectItem key={uf} value={uf} className="text-brand focus:bg-brand/10">
                          {uf}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {'ufCrmv' in errors && errors.ufCrmv?.message && (
                    <p className="text-xs text-destructive">{errors.ufCrmv.message}</p>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-brand font-medium">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={verSenha ? 'text' : 'password'}
                  placeholder="Digite sua senha"
                  disabled={ocupado}
                  autoComplete="current-password"
                  className="bg-surface-raised border-brand/30 focus:border-brand focus:ring-brand/20 placeholder:text-muted-foreground pr-10"
                  {...register('senha')}
                />
                <button
                  type="button"
                  onClick={() => setVerSenha((v) => !v)}
                  aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand/50 hover:text-brand transition-colors"
                >
                  {verSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.senha?.message && (
                <p className="text-xs text-destructive">{errors.senha.message}</p>
              )}
              <div className="flex justify-end">
                <Link
                  to={ROTAS.esqueciSenha}
                  className="text-sm text-brand hover:text-brand/80 hover:underline"
                >
                  Esqueceu sua senha?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-brand hover:bg-brand/80 text-white shadow-lg hover:shadow-xl transition-all"
              disabled={ocupado}
            >
              {ocupado ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </Button>

            <div className="text-center text-sm text-muted-foreground mt-4">
              Não tem uma conta?{' '}
              <Link
                to={ROTAS.registro}
                className="text-brand hover:text-brand/80 font-semibold hover:underline"
              >
                Criar conta
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
